import {css} from '@emotion/css';
import {
  CustomerItem,
  CustomerItemStatus,
  marketToTimezone,
  renderDate,
  ResaleType,
} from '@remoov/functional';
import {Button, theme} from '@remoov/remoov-components';
import React from 'react';
import {
  BoldInfoText,
  CustomerItemStatusWithBadge,
  HideOnLargeScreen,
  HideOnSmallScreen,
  ImageBox,
  InfoText,
  ItemDetails,
  ItemDetailsContainer,
  ItemDetailsOpenContainer,
  ItemsBoardColumn,
  ItemsBoardContainer,
  ItemsBoardDivisionLine,
  ItemsBoardLeftContainer,
  ItemsBoardRightContainer,
  ItemsBoardRow,
} from './Common';

export type AuctionBoardItemProps = {
  item: CustomerItem;
  originalItem: CustomerItem;
  resaleTypeBadge: Maybe<ResaleType>;
  itemDetailsClick: () => unknown;
  setPublicNotes: (value: string) => unknown;
  savePublicNotes: () => unknown;
};

export const AuctionBoardItem = ({
  item,
  originalItem,
  resaleTypeBadge,
  itemDetailsClick,
  setPublicNotes,
  savePublicNotes,
}: AuctionBoardItemProps) => {
  const price = item.amount || item.estimate_resale_value || '';
  const statusToShowPrice = [
    CustomerItemStatus.AUCTIONED,
    CustomerItemStatus.FOR_SALE,
    CustomerItemStatus.SOLD,
    CustomerItemStatus.PENDING_SALE,
    CustomerItemStatus.PAID,
  ];
  const statusToHideNotes = [
    CustomerItemStatus.SOLD,
    CustomerItemStatus.PENDING_SALE,
    CustomerItemStatus.PAID,
  ];
  const timezone = marketToTimezone(item.market);
  return (
    <ItemsBoardContainer className={css({backgroundColor: theme.colors.white})}>
      <ItemsBoardRow className={css({padding: `${theme.spacings.small} 0`})}>
        <HideOnSmallScreen className={css({width: 'auto'})}>
          <ImageBox
            onClick={itemDetailsClick}
            className={css({margin: `0 ${theme.spacings.large}`})}
          >
            <img src={item.small_url || ''} alt={item.description || ''} />
          </ImageBox>
        </HideOnSmallScreen>

        <ItemsBoardColumn>
          <ItemsBoardRow
            className={css({
              [theme.mediaMaxWidth.large]: {
                flexWrap: 'wrap',
                padding: `0 ${theme.spacings.xlarge}`,
              },
            })}
          >
            <HideOnLargeScreen
              className={css({
                marginRight: theme.spacings.large,
                [theme.mediaMaxWidth.small_medium]: {
                  width: '100%',
                  margin: 0,
                  display: 'flex',
                  justifyContent: 'center',
                },
              })}
            >
              <ImageBox onClick={itemDetailsClick}>
                <img src={item.small_url || ''} alt={item.description || ''} />
              </ImageBox>
            </HideOnLargeScreen>

            <ItemsBoardLeftContainer
              className={css({
                [theme.mediaMaxWidth.large]: {
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  padding: theme.spacings.xxsmall,
                },
                [theme.mediaMaxWidth.medium]: {width: 'auto'},
              })}
            >
              <ItemsBoardColumn className={css({maxWidth: '100px'})}>
                <BoldInfoText
                  className={css({
                    [theme.mediaMaxWidth.large]: {marginBottom: theme.spacings.xxsmall},
                  })}
                >
                  {item.id}
                </BoldInfoText>
              </ItemsBoardColumn>
              <ItemsBoardColumn>
                <InfoText>{item.description}</InfoText>
              </ItemsBoardColumn>
            </ItemsBoardLeftContainer>

            <HideOnSmallScreen>
              <ItemsBoardRightContainer>
                <ItemsBoardColumn>
                  <InfoText>{item.quantity}</InfoText>
                </ItemsBoardColumn>
                <CustomerItemStatusWithBadge
                  status={item.status}
                  resaleTypeBadge={resaleTypeBadge}
                />
                <ItemsBoardColumn>
                  {statusToShowPrice.includes(item.status) && <BoldInfoText>{price}</BoldInfoText>}
                </ItemsBoardColumn>
                <ItemsBoardColumn>
                  <InfoText>{renderDate(item.end_of_consignment, timezone)}</InfoText>
                </ItemsBoardColumn>
              </ItemsBoardRightContainer>
            </HideOnSmallScreen>

            <HideOnLargeScreen className={css({padding: theme.spacings.xxsmall})}>
              <ItemsBoardColumn className={css({div: {marginBottom: theme.spacings.xxsmall}})}>
                <ItemsBoardRow>
                  <BoldInfoText>Quantity: </BoldInfoText>
                  <InfoText>{item.quantity}</InfoText>
                </ItemsBoardRow>
                <ItemsBoardRow>
                  <BoldInfoText>Status: </BoldInfoText>
                  <CustomerItemStatusWithBadge
                    status={item.status}
                    resaleTypeBadge={resaleTypeBadge}
                  />
                </ItemsBoardRow>
                {statusToShowPrice.includes(item.status) && (
                  <ItemsBoardRow>
                    <BoldInfoText>Sales Price: </BoldInfoText>
                    <InfoText className={css({color: '#2E3192', fontWeight: theme.font.bold})}>
                      {price}
                    </InfoText>
                  </ItemsBoardRow>
                )}
              </ItemsBoardColumn>
            </HideOnLargeScreen>
          </ItemsBoardRow>
          <ItemsBoardRow>
            <ItemDetailsContainer>
              <ItemDetailsOpenContainer>
                {!statusToHideNotes.includes(item.status) && (
                  <>
                    <ItemDetails
                      placeholder='New notes...'
                      value={item.public_notes || ''}
                      onChange={event => setPublicNotes(event.target.value)}
                    />
                    <Button
                      buttonStyle='secondary'
                      minimal
                      onClick={savePublicNotes}
                      className={css({
                        marginLeft: '16px',
                        width: '80px',
                        visibility:
                          item.public_notes === originalItem.public_notes ? 'hidden' : undefined,
                      })}
                    >
                      Save notes
                    </Button>
                  </>
                )}
              </ItemDetailsOpenContainer>
              <Button buttonStyle='secondary' onClick={itemDetailsClick}>
                Item Details
              </Button>
            </ItemDetailsContainer>
          </ItemsBoardRow>
        </ItemsBoardColumn>
      </ItemsBoardRow>
      <ItemsBoardDivisionLine />
    </ItemsBoardContainer>
  );
};
